/**
 * Durable FIFO queue for captured audio, backed by IndexedDB.
 *
 * Why this exists: `react-use-websocket` already buffers frames sent while the
 * socket is closed, but that buffer lives in a ref — it grows without bound (a
 * ten-minute outage is ~19 MB of PCM) and vanishes on reload or a tab crash. A
 * doctor mid-consultation gets no warning either way.
 *
 * So every captured chunk lands here first and the socket is fed *only* from
 * this queue, online or offline. Two properties matter:
 *
 *  - **Strict FIFO.** Live audio never jumps ahead of a backlog. The server
 *    appends chunks in arrival order, so overtaking would scramble both the
 *    recording and the transcript.
 *  - **Delete only after send.** A chunk leaves the queue once the socket has
 *    accepted it, so a drop mid-drain leaves the remainder on disk.
 *
 * Chunks are ~4 KB of raw PCM16 (16 kHz mono), so roughly 32 KB per second.
 */

const DB_NAME = "vetic-voice-audio";
const DB_VERSION = 1;
const STORE = "chunks";
const EMR_INDEX = "emrId";

/** Hard ceiling on backlog, ~60 minutes of audio at 32 KB/s. */
export const AUDIO_QUEUE_MAX_BYTES = 115 * 1024 * 1024;

export interface QueuedChunk {
  id: number;
  data: ArrayBuffer;
}

export interface QueueStats {
  count: number;
  bytes: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const openDatabase = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // autoIncrement ids are monotonic, which is what gives us FIFO for free.
        const store = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        store.createIndex(EMR_INDEX, "emrId", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  // Don't cache a rejected promise — a later attempt should be able to retry.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
};

const runTransaction = <T,>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, resolve: (value: T) => void) => void,
): Promise<T> =>
  openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        let result: T;
        work(tx.objectStore(STORE), (value) => {
          result = value;
        });
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );

export const enqueueAudioChunk = (emrId: string, data: ArrayBuffer): Promise<void> =>
  runTransaction<void>("readwrite", (store) => {
    store.add({ emrId, data, size: data.byteLength });
  });

/** Oldest chunks first. `limit` caps how much is held in memory at once. */
export const peekAudioChunks = (emrId: string, limit: number): Promise<QueuedChunk[]> =>
  runTransaction<QueuedChunk[]>("readonly", (store, resolve) => {
    const chunks: QueuedChunk[] = [];
    const request = store.index(EMR_INDEX).openCursor(IDBKeyRange.only(emrId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor || chunks.length >= limit) {
        resolve(chunks);
        return;
      }
      chunks.push({ id: cursor.value.id, data: cursor.value.data });
      cursor.continue();
    };
  });

export const deleteAudioChunks = (ids: number[]): Promise<void> =>
  runTransaction<void>("readwrite", (store) => {
    ids.forEach((id) => store.delete(id));
  });

export const getQueueStats = (emrId: string): Promise<QueueStats> =>
  runTransaction<QueueStats>("readonly", (store, resolve) => {
    let count = 0;
    let bytes = 0;
    const request = store.index(EMR_INDEX).openCursor(IDBKeyRange.only(emrId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve({ count, bytes });
        return;
      }
      count += 1;
      bytes += cursor.value.size ?? cursor.value.data?.byteLength ?? 0;
      cursor.continue();
    };
  });

export const clearAudioQueue = (emrId: string): Promise<void> =>
  runTransaction<void>("readwrite", (store) => {
    const request = store.index(EMR_INDEX).openCursor(IDBKeyRange.only(emrId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
  });
